// src/pages/AlarmMonitoring.jsx

import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

export default function AlarmMonitoring() {
  return (
    <div className="min-h-screen bg-[#111111] text-white">

      <Helmet>
        <title>Professional Alarm Monitoring | SeguraTech</title>

        <meta
          name="description"
          content="Professional alarm monitoring for homes and businesses, providing an additional layer of security through our trusted monitoring partner, Arc Monitoring."
        />

        <meta
          property="og:title"
          content="Professional Alarm Monitoring | SeguraTech"
        />

        <meta
          property="og:description"
          content="Professional alarm monitoring from SeguraTech."
        />

        <meta property="og:type" content="website" />

        <meta
          property="og:url"
          content="https://www.seguratech.co.uk/alarms/monitoring"
        />

        <meta property="og:image" content="/ajax-ready.png" />
      </Helmet>

      {/* Hero */}

      <section className="max-w-5xl mx-auto px-6 pt-24 pb-20 text-center">

        <h1 className="text-4xl md:text-6xl font-extrabold text-[#C6A564] mb-8">
          Professional Alarm Monitoring
        </h1>

        <p className="text-xl text-white/80 max-w-3xl mx-auto leading-8">
          Professional alarm monitoring provides an additional layer of
          security by ensuring your alarm system continues to be monitored,
          even when you're unable to respond yourself.
        </p>

      </section>

      {/* Main Content */}

      <section className="max-w-5xl mx-auto px-6 pb-24 space-y-20">

        {/* What Is Monitoring */}

        <div>

          <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
            What Is Professional Alarm Monitoring?
          </h2>

          <div className="space-y-6 text-white/80 leading-8">

            <p>
              Most modern alarm systems can send a notification directly to
              your mobile phone when an alarm activates. While this is an
              excellent feature, there may be times when you're unable to
              respond immediately.
            </p>

            <p>
              Professional monitoring adds another level of protection by
              connecting your alarm system to a dedicated monitoring centre.
              If your alarm activates, trained operators also receive the
              alarm signal and follow the agreed procedures for your property.
            </p>

            <p>
              SeguraTech works with{" "}
              <a
                href="https://www.arcmon.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#C6A564] hover:underline"
              >
                Arc Monitoring
              </a>
              , an established UK Alarm Receiving Centre providing professional
              monitoring services for residential and commercial properties.
            </p>

          </div>

        </div>

        {/* What Happens */}

        <div>

          <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
            What Happens When My Alarm Activates?
          </h2>

          <div className="space-y-6 text-white/80 leading-8">

            <p>
              If your alarm detects an intrusion or another important event,
              it immediately sends a secure signal to{" "}
              <a
                href="https://www.arcmon.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#C6A564] hover:underline"
              >
                Arc Monitoring
              </a>
              .
            </p>

            <p>
              Trained operators receive the alarm and follow the monitoring
              instructions agreed for your property. Depending on the
              monitoring package you choose, this may include contacting you
              or your nominated keyholders so the appropriate action can be
              taken.
            </p>

            <p>
              Every property is different, which is why monitoring
              arrangements are tailored to suit your individual requirements.
            </p>

          </div>

        </div>

        {/* Designed to Stay Connected */}

        <div>

          <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
            Designed to Stay Connected
          </h2>

          <div className="space-y-6 text-white/80 leading-8">

            <p>
              A professionally monitored alarm is only effective if it can
              communicate reliably.
            </p>

            <p>
              Ajax alarm systems are designed with resilience in mind.
              Where available, the system can automatically switch between
              different communication methods if one connection becomes
              unavailable, helping it remain connected to{" "}
              <a
                href="https://www.arcmon.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#C6A564] hover:underline"
              >
                Arc Monitoring
              </a>
              .
            </p>

            <p>
              This additional resilience helps ensure important alarm events
              continue to be transmitted when they're needed most.
            </p>

          </div>

        </div>

        {/* Monitoring Partner */}

        <div>

          <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
            Monitoring You Can Rely On
          </h2>

          <div className="space-y-6 text-white/80 leading-8">

            <p>
              By working with{" "}
              <a
                href="https://www.arcmon.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#C6A564] hover:underline"
              >
                Arc Monitoring
              </a>
              , SeguraTech is able to offer professional monitoring services
              backed by an experienced UK monitoring centre.
            </p>

            <p>
              Combined with Ajax's intelligent security technology, professional
              monitoring provides an additional level of reassurance whenever
              you're away from your property.
            </p>

            <p>
              We'll explain the available monitoring options and help you
              choose the solution that's right for your property and your
              individual requirements.
            </p>

          </div>

        </div>

        {/* Suitable For */}

        <div>

          <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
            Suitable For
          </h2>

          <div className="space-y-6 text-white/80 leading-8">

            <p>
              Professional monitoring is suitable for almost any property,
              whether you're protecting your home, your business or a building
              that is regularly left unoccupied.
            </p>

            <ul className="space-y-3 ml-6 list-disc">

              <li>Homes</li>
              <li>Apartments</li>
              <li>Holiday homes</li>
              <li>Retail premises</li>
              <li>Offices</li>
              <li>Workshops</li>
              <li>Warehouses</li>
              <li>Commercial buildings</li>

            </ul>

            <p>
              Whether you're away for a few hours or several weeks,
              professional monitoring provides additional confidence that
              your alarm system continues to be monitored when you're unable
              to respond yourself.
            </p>

          </div>

        </div>

        {/* CTA */}

        <div className="border border-[#C6A564] rounded-2xl bg-[#1a1a1a] p-12 text-center">

          <h2 className="text-3xl font-bold text-[#C6A564] mb-6">
            Ready to Learn More?
          </h2>

          <p className="text-white/80 leading-8 max-w-3xl mx-auto">
            Whether you're considering a new Ajax alarm system or adding
            professional monitoring to an existing installation, we'll explain
            the available options and help you choose the solution that's right
            for your property.
          </p>

          <Link
            to="/contact"
            className="inline-block mt-10 bg-[#C6A564] text-[#111111] font-semibold px-10 py-4 rounded-xl hover:opacity-90 transition"
          >
            Contact SeguraTech
          </Link>

        </div>

      </section>

    </div>
  );
}